import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import {
  Loader2,
  Save,
  Cloud,
  Wrench,
  CheckCircle2,
  AlertTriangle,
  Plus,
  Trash2,
  Pencil,
  X,
  Bot,
  Plug,
  Upload,
  Activity,
  User,
} from "lucide-react";
import Agents from "./Agents";
import Tools from "./Tools";
import UploadPage from "./Upload";
import Tokens from "./Tokens";
import Profile from "./Profile";

interface OllamaSettings {
  base_url: string;
  api_key: string;
  max_concurrent: number;
  allowed_models: string[];
}

interface Skill {
  id?: number;
  name: string;
  title: string;
  description?: string;
  instructions?: string;
  enabled: boolean;
  built_in?: boolean;
}

interface SettingsData {
  ollama_cloud: OllamaSettings;
  skills: { name: string; enabled: boolean }[];
}

const TABS = [
  { key: "models", label: "Models API", icon: Cloud },
  { key: "skills", label: "Skills", icon: Wrench },
  { key: "agents", label: "Agents", icon: Bot },
  { key: "tools", label: "Tools", icon: Plug },
  { key: "upload", label: "Upload Reports", icon: Upload },
  { key: "tokens", label: "Token Usage", icon: Activity },
  { key: "profile", label: "Profile", icon: User },
];

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "models";

  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [skills, setSkills] = useState<Skill[]>([]);
  const [editing, setEditing] = useState<Skill | null>(null);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    Promise.all([api.getSettings(), api.listSkills()])
      .then(([s, sl]) => {
        if (s && s.ollama_cloud && Array.isArray(s.skills)) {
          setSettings(s);
        } else {
          setError("Unexpected settings format from server.");
        }
        setSkills(sl.skills || []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load settings."))
      .finally(() => setLoading(false));
  }, []);

  const setTab = (tab: string) => {
    setSearchParams({ tab });
  };

  const saveOllama = async () => {
    if (!settings) return;
    setSaving(true);
    setMessage(null);
    try {
      const data = await api.saveOllamaSettings(settings.ollama_cloud);
      if (data && data.saved) {
        setMessage({ type: "success", text: "Models API settings saved." });
      } else {
        throw new Error("Save failed");
      }
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const saveSkills = async () => {
    if (!settings || !skills.length) return;
    setSaving(true);
    setMessage(null);
    try {
      const toggles = skills.map((s) => ({ name: s.name, enabled: s.enabled }));
      const data = await api.saveSkills(toggles);
      if (data && data.saved) {
        setSettings({ ...settings, skills: toggles });
        setMessage({ type: "success", text: "Skill toggles saved." });
      } else {
        throw new Error("Save failed");
      }
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const updateSkillToggle = (name: string, enabled: boolean) => {
    setSkills(skills.map((s) => (s.name === name ? { ...s, enabled } : s)));
  };

  const updateAllowedModels = (value: string) => {
    if (!settings) return;
    setSettings({
      ...settings,
      ollama_cloud: {
        ...settings.ollama_cloud,
        allowed_models: value.split(",").map((m) => m.trim()).filter(Boolean),
      },
    });
  };

  const startNewSkill = () => {
    setEditing({ name: "", title: "", description: "", instructions: "", enabled: true, built_in: false });
    setIsNew(true);
  };

  const startEditSkill = (skill: Skill) => {
    setEditing({ ...skill });
    setIsNew(false);
  };

  const cancelEdit = () => {
    setEditing(null);
    setIsNew(false);
  };

  const saveSkill = async () => {
    if (!editing) return;
    setSaving(true);
    setMessage(null);
    try {
      let data;
      if (isNew) {
        data = await api.createSkill(editing);
      } else {
        data = await api.updateSkill(editing.name, editing);
      }
      if (data && data.saved) {
        const updated = await api.listSkills();
        setSkills(updated.skills || []);
        setEditing(null);
        setIsNew(false);
        setMessage({ type: "success", text: isNew ? "Skill created." : "Skill updated." });
      } else {
        throw new Error("Save failed");
      }
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const deleteSkill = async (name: string) => {
    if (!confirm(`Delete skill "${name}"?`)) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.deleteSkill(name);
      const updated = await api.listSkills();
      setSkills(updated.skills || []);
      setMessage({ type: "success", text: "Skill deleted." });
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Delete failed" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-novacare-700" />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Settings</h1>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg border border-red-200 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error || "Could not load settings."}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Settings</h1>
      <p className="text-slate-500 mb-6">Manage AI models, skills, agents, tools and your account.</p>

      {message && (
        <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm mb-6 ${
          message.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          {message.type === "success" ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 mb-6 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? "text-novacare-700 border-b-2 border-novacare-700"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "models" && (
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Cloud className="w-5 h-5 text-novacare-700" />
            <h2 className="text-lg font-semibold text-slate-900">Models API</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Base URL</label>
              <input
                type="text"
                value={settings.ollama_cloud.base_url}
                onChange={(e) => setSettings({ ...settings, ollama_cloud: { ...settings.ollama_cloud, base_url: e.target.value } })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
              <input
                type="password"
                value={settings.ollama_cloud.api_key}
                onChange={(e) => setSettings({ ...settings, ollama_cloud: { ...settings.ollama_cloud, api_key: e.target.value } })}
                placeholder="Set via OLLAMA_CLOUD_API_KEY env var"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Max concurrent requests</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={settings.ollama_cloud.max_concurrent}
                  onChange={(e) => setSettings({ ...settings, ollama_cloud: { ...settings.ollama_cloud, max_concurrent: parseInt(e.target.value) || 1 } })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Allowed models (comma separated)</label>
                <input
                  type="text"
                  value={settings.ollama_cloud.allowed_models.join(", ")}
                  onChange={(e) => updateAllowedModels(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700"
                />
              </div>
            </div>
          </div>
          <button
            onClick={saveOllama}
            disabled={saving}
            className="mt-6 inline-flex items-center gap-2 bg-novacare-700 hover:bg-novacare-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Models API Settings
          </button>
        </section>
      )}

      {activeTab === "skills" && (
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-novacare-700" />
              <h2 className="text-lg font-semibold text-slate-900">Skills</h2>
            </div>
            <button
              onClick={startNewSkill}
              className="inline-flex items-center gap-1 text-sm text-novacare-700 hover:text-novacare-800 font-medium"
            >
              <Plus className="w-4 h-4" /> New skill
            </button>
          </div>

          <div className="space-y-3">
            {skills.map((skill) => (
              <div key={skill.name} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                <div className="flex-1 min-w-0 mr-4">
                  <p className="font-medium text-slate-800">{skill.title || skill.name}</p>
                  <p className="text-xs text-slate-500 truncate">{skill.description || ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="relative inline-flex items-center cursor-pointer mr-2">
                    <input
                      type="checkbox"
                      checked={skill.enabled}
                      onChange={(e) => updateSkillToggle(skill.name, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-novacare-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-novacare-700"></div>
                  </label>
                  <button
                    onClick={() => startEditSkill(skill)}
                    className="p-1.5 text-slate-400 hover:text-novacare-700 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Edit skill"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {!skill.built_in && (
                    <button
                      onClick={() => deleteSkill(skill.name)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete skill"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={saveSkills}
            disabled={saving}
            className="mt-6 inline-flex items-center gap-2 bg-novacare-700 hover:bg-novacare-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Skill Toggles
          </button>
        </section>
      )}

      {activeTab === "agents" && <Agents />}
      {activeTab === "tools" && <Tools />}
      {activeTab === "upload" && <UploadPage embedded />}
      {activeTab === "tokens" && <Tokens />}
      {activeTab === "profile" && <Profile />}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">{isNew ? "New Skill" : "Edit Skill"}</h3>
              <button onClick={cancelEdit} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name (unique id)</label>
                <input
                  type="text"
                  value={editing.name}
                  disabled={!isNew}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700 disabled:bg-slate-100"
                />
                <p className="text-xs text-slate-500 mt-1">Lowercase, letters, numbers, - and _ only. Cannot be changed after creation.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Instructions</label>
                <textarea
                  value={editing.instructions || ""}
                  onChange={(e) => setEditing({ ...editing, instructions: e.target.value })}
                  rows={10}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700 text-sm font-mono"
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                />
                <span className="text-sm text-slate-700">Enabled</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
              <button onClick={cancelEdit} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
              <button
                onClick={saveSkill}
                disabled={saving || !editing.name || !editing.title}
                className="inline-flex items-center gap-2 px-4 py-2 bg-novacare-700 hover:bg-novacare-800 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Skill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
